/**
 * Верхний preview-блок внутри сообщения Activity.
 * Показывает ссылку на scope и/или текст родительского комментария (reply),
 * чтобы пользователь видел контекст ответа.
 */
import { Text } from '@mantine/core';
import type { ActivityChatItem } from '@ayocore/activity/common/types/activity-message';
import { useActivityScopeActions } from '../../hooks/useActivityScopeActions';
import { getActivityScopeBadgeText } from '../utils/activity-scope-label';
import classes from './ActivityChatMessageItem.module.css';

type ActivityChatReplyHeaderProps = {
  item: ActivityChatItem;
};

/**
 * Отображает unified-preview в балуне сообщения:
 * - scope как ссылку;
 * - текст reply;
 * - если есть оба фрагмента, объединяет их через `—`.
 */
export function ActivityChatReplyHeader({ item }: ActivityChatReplyHeaderProps) {
  const scopeText = getActivityScopeBadgeText(item.scope);
  const replyText = item.replyToMessage?.preview?.trim() ?? '';
  const { onScopeClick, isScopeClickable } = useActivityScopeActions();
  const scopeClickable = isScopeClickable(item.scope);

  if (!scopeText && !replyText) {
    return null;
  }

  return (
    <div
      className={classes.replyPreview}
      title={[scopeText, replyText].filter(Boolean).join(' — ')}
    >
      {scopeText ? (
        <Text
          component='span'
          size='xs'
          className={`${classes.replyScopeLink} ${scopeClickable ? classes.replyScopeLinkClickable : ''}`}
          title={scopeText}
          role={scopeClickable ? 'button' : undefined}
          tabIndex={scopeClickable ? 0 : undefined}
          onClick={
            scopeClickable
              ? () => {
                  void onScopeClick(item.scope);
                }
              : undefined
          }
          onKeyDown={
            scopeClickable
              ? event => {
                  if (event.key === 'Enter' || event.key === ' ') {
                    event.preventDefault();
                    void onScopeClick(item.scope);
                  }
                }
              : undefined
          }
        >
          {scopeText}
        </Text>
      ) : null}
      {scopeText && replyText ? (
        <Text component='span' size='xs' className={classes.replyDelimiter} aria-hidden>
          {' — '}
        </Text>
      ) : null}
      {replyText ? (
        <Text component='span' size='xs' className={classes.replyText} title={replyText}>
          {replyText}
        </Text>
      ) : null}
    </div>
  );
}
